import { createServerFn } from '@tanstack/react-start'
import { getRequest } from '@tanstack/react-start/server'
import { z } from 'zod'
import { auth } from '@server/auth'
import { getEffectiveUser } from '@server/identity'
import {
  createPlanCheckRun,
  getPlanCheckRunForOwner,
  listPlanCheckRunsForOwner,
  restartPlanCheckRunFromScratch,
  resumePlanCheckRun,
  schedulePlanCheckProcessing,
} from '@server/plan-check-service'

export const PLAN_CHECK_ACCESS_DENIED_ERROR = 'PLAN_CHECK_ACCESS_DENIED'

async function currentOwner() {
  const request = getRequest()
  const session = await auth.api.getSession({ headers: request.headers })
  const effUser = await getEffectiveUser(session?.user ?? null, request.headers)
  return {
    userId: effUser.type === 'user' ? effUser.id : null,
    anonymousId: effUser.type === 'anonymous' ? effUser.id : null,
  }
}

export const createPlanCheck = createServerFn({ method: 'POST' })
  .inputValidator((value: unknown) => value as FormData)
  .handler(async ({ data }) => {
    const owner = await currentOwner()
    const file = data.get('file')
    if (!(file instanceof File)) {
      throw new Error('A PDF file is required.')
    }
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      throw new Error('Only PDF files are supported for plan checks.')
    }
    const title = String(data.get('title') || file.name)
    const projectMetadata = {
      buildingType: String(data.get('buildingType') || ''),
      workType: String(data.get('workType') || ''),
      notes: String(data.get('notes') || ''),
      jurisdictionMode: 'all_uk_and_corpus',
    }
    const run = await createPlanCheckRun({
      ...owner,
      title,
      filename: file.name,
      fileBytes: Buffer.from(await file.arrayBuffer()),
      projectMetadata,
    })
    return { id: run.id, status: run.status }
  })

export const listPlanChecks = createServerFn({ method: 'GET' }).handler(
  async () => {
    const owner = await currentOwner()
    return listPlanCheckRunsForOwner(owner)
  },
)

export const getPlanCheck = createServerFn({ method: 'POST' })
  .inputValidator(z.object({ runId: z.string() }))
  .handler(async ({ data }) => {
    const owner = await currentOwner()
    const detail = await getPlanCheckRunForOwner(data.runId, owner)
    if (!detail) {
      throw new Error(PLAN_CHECK_ACCESS_DENIED_ERROR)
    }
    return detail
  })

export const retryPlanCheck = createServerFn({ method: 'POST' })
  .inputValidator(z.object({ runId: z.string() }))
  .handler(async ({ data }) => {
    const owner = await currentOwner()
    const detail = await getPlanCheckRunForOwner(data.runId, owner)
    if (!detail) {
      throw new Error(PLAN_CHECK_ACCESS_DENIED_ERROR)
    }
    if (detail.run.status === 'failed' || detail.run.status === 'processing') {
      await resumePlanCheckRun(detail)
      schedulePlanCheckProcessing(data.runId)
    } else {
      schedulePlanCheckProcessing(data.runId)
    }
    return { id: data.runId, status: 'queued' }
  })

export const restartPlanCheck = createServerFn({ method: 'POST' })
  .inputValidator(z.object({ runId: z.string() }))
  .handler(async ({ data }) => {
    const owner = await currentOwner()
    const detail = await getPlanCheckRunForOwner(data.runId, owner)
    if (!detail) {
      throw new Error(PLAN_CHECK_ACCESS_DENIED_ERROR)
    }
    await restartPlanCheckRunFromScratch(detail)
    schedulePlanCheckProcessing(data.runId)
    return { id: data.runId, status: 'queued' }
  })
